import { Component } from "@theme/component";
import {
  ThemeEvents,
  VariantUpdateEvent,
  SlideshowSelectEvent,
} from "@theme/events";

/**
 * @typedef {object} GalleryRefs
 * @property {import('./slideshow').Slideshow} [mobileSlideshow]
 * @property {HTMLImageElement[]} [desktopSlides]
 * @property {HTMLButtonElement[]} [dotButtons]
 * @property {HTMLButtonElement[]} [thumbnailButtons]
 */

/**
 * Handles syncing the custom Liquid gallery between desktop and mobile layouts.
 *
 * @extends {Component<GalleryRefs>}
 */
class GRProductGallery extends Component {
  /** @type {AbortController | null} */
  #controller = null;

  #currentIndex = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#currentIndex = this.#normalizeIndex(this.initialIndex);

    this.#attachEventListeners();
    this.#render();

    const { signal } = this.#controller;
    const target = this.closest(".shopify-section, dialog");
    target?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
      { signal },
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  get initialIndex() {
    const raw = this.dataset.initialIndex;
    const parsed = raw == null ? 0 : parseInt(raw, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  get slideCount() {
    return (
      this.refs.desktopSlides?.length ??
      this.refs.mobileSlideshow?.slides?.length ??
      0
    );
  }

  #attachEventListeners() {
    if (!this.#controller) return;

    const { signal } = this.#controller;

    this.refs.dotButtons?.forEach((button) => {
      button.addEventListener("click", this.#handleControlClick, { signal });
    });

    this.refs.thumbnailButtons?.forEach((button) => {
      button.addEventListener("click", this.#handleControlClick, { signal });
    });

    const { mobileSlideshow } = this.refs;
    if (mobileSlideshow) {
      mobileSlideshow.addEventListener(
        SlideshowSelectEvent.eventName,
        this.#handleSlideshowSelect,
        { signal },
      );
    }
  }

  /**
   * Handles a variant update event by replacing the current gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newGallery = source.querySelector("gr-product-gallery");

    if (!newGallery) return;

    this.replaceWith(newGallery);
  };

  /**
   * @param {MouseEvent} event
   */
  #handleControlClick = (event) => {
    const target = event.currentTarget;
    if (!(target instanceof HTMLElement)) return;
    const index = parseInt(target.dataset.index ?? "", 10);
    if (Number.isNaN(index)) return;
    this.#setIndex(index);
  };

  /**
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    const { index } = event.detail;
    if (index !== this.#currentIndex) {
      this.#currentIndex = this.#normalizeIndex(index);
      this.#updateControls();
      this.#updateDesktopSlides();
    }
  };

  /**
   * @param {number} index
   */
  #setIndex(index) {
    const nextIndex = this.#normalizeIndex(index);
    this.#currentIndex = nextIndex;
    this.#render();

    const { mobileSlideshow } = this.refs;
    if (mobileSlideshow) {
      mobileSlideshow.select(nextIndex);
    }
  }

  /**
   * @param {number} index
   */
  #normalizeIndex(index) {
    const count = this.slideCount;
    if (count === 0) return 0;

    if (index >= count) return 0;
    if (index < 0) return count - 1;
    return index;
  }

  #render() {
    this.#updateDesktopSlides();
    this.#updateControls();
  }

  #updateDesktopSlides() {
    this.refs.desktopSlides?.forEach((slide, index) => {
      const isActive = index === this.#currentIndex;
      slide.style.opacity = isActive ? "1" : "0";
      slide.style.pointerEvents = isActive ? "" : "none";
      slide.setAttribute("aria-hidden", String(!isActive));
    });
  }

  #updateControls() {
    this.refs.dotButtons?.forEach((button, index) => {
      const isActive = index === this.#currentIndex;
      button.setAttribute("aria-selected", String(isActive));
      this.#setDotState(button, isActive);
    });

    this.refs.thumbnailButtons?.forEach((button, index) => {
      const isActive = index === this.#currentIndex;
      button.setAttribute("aria-selected", String(isActive));
      this.#setThumbnailState(button, isActive);
    });
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {boolean} isActive
   */
  #setDotState(button, isActive) {
    button.classList.toggle("bg-black-warm", isActive);
    button.classList.toggle("border-black-warm", true);
    button.classList.toggle("bg-transparent", !isActive);
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {boolean} isActive
   */
  #setThumbnailState(button, isActive) {
    button.classList.toggle("border-black-warm", isActive);
    button.classList.toggle("border-transparent", !isActive);

    const thumbnailImage = button.querySelector("img");
    if (thumbnailImage instanceof HTMLElement) {
      thumbnailImage.classList.toggle("ring-2", isActive);
      thumbnailImage.classList.toggle("ring-white", isActive);
      thumbnailImage.classList.toggle("ring-offset-1", isActive);
      thumbnailImage.classList.toggle("ring-transparent", !isActive);
    }
  }
}

if (!customElements.get("gr-product-gallery")) {
  customElements.define("gr-product-gallery", GRProductGallery);
}
